/**
 * The application root: server-state cache, theme, the app-level error
 * boundary and the router.
 *
 * The theme preference lives here because the navbar switch and the settings
 * screen both change it, and because the resolved value has to reach the
 * document element and the stored hint (research §22). The error boundary
 * catches what the router's own boundary cannot: a failure outside the routes
 * altogether.
 *
 * @author John Grimes
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { RouterProvider, createBrowserRouter } from "react-router";

import { AppErrorScreen } from "./components/AppErrorScreen";
import {
  ThemeContext,
  initialThemePreference,
  useResolvedTheme,
} from "./lib/theme";
import { appRoutes } from "./routes";

import type { ThemeContextValue } from "./lib/theme";
import type { JSX } from "react";
import type { ThemePreference } from "shared/api";

/** How long server state stays fresh before it is fetched again. */
const STALE_TIME_MS = 30_000;

const router = createBrowserRouter(appRoutes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Failures are explained rather than retried behind the user's back; the
      // screens that can retry offer a button for it (Principle V).
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
    },
  },
});

/**
 * Renders the application shell.
 *
 * @returns The root element of the app.
 */
export const App = (): JSX.Element => {
  // The account's own preference replaces this as soon as its settings load;
  // until then the stored hint is the closest guess (FR-056).
  const [preference, setPreference] = useState<ThemePreference>(
    initialThemePreference,
  );
  const resolvedTheme = useResolvedTheme(preference);
  const theme = useMemo<ThemeContextValue>(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContext value={theme}>
        <ErrorBoundary FallbackComponent={AppErrorScreen}>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </ThemeContext>
    </QueryClientProvider>
  );
};
